import { useState, useCallback } from "react";
import { toast } from "sonner";
import jsPDF from "jspdf";

/**
 * Custom hook for managing export dialog state and functionality
 * Reduces code duplication across report pages
 * 
 * @returns {Object} - Hook state and handlers
 */
const useExportDialog = () => {
  const [exportDialogOpen, setExportDialogOpen] = useState(false);

  const openExportDialog = useCallback(() => {
    setExportDialogOpen(true);
  }, []);

  const closeExportDialog = useCallback(() => {
    setExportDialogOpen(false);
  }, []);

  /**
   * Export data to CSV file
   * @param {string} filename - Name of the file without extension
   * @param {string} csvContent - CSV content string
   */
  const exportToExcel = useCallback((filename, csvContent) => {
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${filename}-${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success("Excel export downloaded");
  }, []);

  /**
   * Export to PDF as a downloadable file
   * @param {string} title - Title of the document
   * @param {string} htmlContent - HTML content to print (body content only)
   * @param {string} customStyles - Additional CSS styles
   */
  const exportToPdf = useCallback(async (title, htmlContent, customStyles = "") => {
    toast.info("Generating PDF...");

    // Create an off-screen container to render the HTML
    const container = document.createElement("div");
    container.style.position = "absolute";
    container.style.left = "-9999px";
    container.style.top = "0";
    container.style.width = "800px";
    container.style.background = "#fff";
    container.innerHTML = `
      <div style="font-family: Arial, sans-serif; padding: 20px; margin: 0;">
        <style>
          .company-header {
            display: flex;
            align-items: center;
            gap: 16px;
            padding-bottom: 16px;
            border-bottom: 2px solid #2563eb;
            margin-bottom: 20px;
          }
          .company-header img { width: 50px; height: 50px; object-fit: contain; }
          .company-header-text h1 { font-size: 24px; margin: 0; color: #1e40af; }
          .company-header-text p { font-size: 12px; color: #666; margin: 2px 0 0 0; }
          h1 { font-size: 24px; margin-bottom: 5px; color: #333; }
          h2 { font-size: 18px; margin: 20px 0 10px 0; color: #555; }
          .report-title { font-size: 20px; margin-bottom: 5px; color: #333; }
          .subtitle { color: #666; margin-bottom: 20px; font-size: 14px; }
          table { width: 100%; border-collapse: collapse; margin: 10px 0 20px 0; font-size: 12px; }
          th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
          th { background-color: #f5f5f5; font-weight: bold; }
          tr:nth-child(even) { background-color: #fafafa; }
          .text-right { text-align: right; }
          .text-center { text-align: center; }
          .green { color: #16a34a; }
          .yellow { color: #ca8a04; }
          .red { color: #dc2626; }
          .footer { margin-top: 30px; font-size: 12px; color: #666; text-align: center; border-top: 1px solid #ddd; padding-top: 15px; }
          ${customStyles}
        </style>
        <div class="company-header">
          <div class="company-header-text">
            <h1>V2R Masonry</h1>
            <p>V2R Masonry Systems</p>
          </div>
        </div>
        ${htmlContent}
      </div>
    `;
    document.body.appendChild(container);

    try {
      const html2canvas = (await import("html2canvas")).default;
      const canvas = await html2canvas(container, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: "#ffffff",
      });

      const imgData = canvas.toDataURL("image/jpeg", 0.95);
      const imgWidth = 210; // A4 width in mm
      const pageHeight = 297; // A4 height in mm
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      const pdf = new jsPDF("p", "mm", "a4");
      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(imgData, "JPEG", 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      while (heightLeft > 0) {
        position -= pageHeight;
        pdf.addPage();
        pdf.addImage(imgData, "JPEG", 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      const filename = `${title.replace(/[^a-zA-Z0-9]/g, "_")}-${new Date().toISOString().split("T")[0]}.pdf`;
      pdf.save(filename);
      toast.success("PDF downloaded");
    } catch (err) {
      console.error("PDF generation failed:", err);
      toast.error("PDF generation failed. Falling back to print dialog.");
      // Fallback to print dialog
      const printWindow = window.open('', '_blank');
      if (printWindow) {
        printWindow.document.write(container.innerHTML);
        printWindow.document.close();
        printWindow.focus();
        setTimeout(() => { printWindow.print(); printWindow.close(); }, 250);
      }
    } finally {
      document.body.removeChild(container);
    }
  }, []);

  return {
    exportDialogOpen,
    setExportDialogOpen,
    openExportDialog,
    closeExportDialog,
    exportToExcel,
    exportToPdf,
  };
};

export default useExportDialog;
